import { apiRequest } from "../../../shared/lib/api-client.ts";
import { getEvWorkstationProjectDetail } from "../../ev-workstation/api/ev-workstation-api.ts";
import type { ApiResponse } from "../../leaderboard/types/index.ts";
import type {
  AuditEvidence,
  AuditStatusPayload,
  ProjectDetailData,
  ProjectRankSummary,
} from "../types/index.ts";

interface ProjectAuditRunRecord {
  id: string;
  status: string;
  alpha_score_snapshot: number | null;
  confidence_snapshot: number | null;
  provider_success_count: number;
  provider_fail_count: number;
  started_at: string;
  finished_at: string | null;
  notes?: string;
  trigger_source?: string;
  trigger_flags?: string[];
}

function emptyProjectDetailData(): ProjectDetailData {
  return {
    history: [],
    evidence: [],
    auditStatus: null,
    rankSummary: null,
    workflowDetail: null,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toNumberOrNull(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function toStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => String(item ?? "").trim()).filter(Boolean) : [];
}

function toAuditStatusValue(raw: string | null | undefined): AuditStatusPayload["status"] {
  switch (raw) {
    case "queued":
    case "processing":
    case "completed":
    case "failed":
    case "partial":
      return raw;
    default:
      return "none";
  }
}

async function fetchJson<T>(path: string): Promise<T | null> {
  const response = await apiRequest(path);
  if (!response.ok) {
    return null;
  }

  const payload = (await response.json().catch(() => null)) as ApiResponse<T> | null;
  return payload?.data ?? null;
}

function buildFallbackAuditStatus(
  latestRun: ProjectAuditRunRecord | null,
  evidence: AuditEvidence[],
  workflowDetail: ProjectDetailData["workflowDetail"]
): AuditStatusPayload | null {
  if (!latestRun && evidence.length === 0 && !workflowDetail?.warmLead.hasWarmLead) {
    return null;
  }

  const completed = evidence
    .filter((item) => !item.error_message)
    .map((item) => item.provider_slug);
  const failed = evidence
    .filter((item) => Boolean(item.error_message))
    .map((item) => item.provider_slug);
  const failedDetails = Object.fromEntries(
    evidence
      .filter((item) => item.error_message)
      .map((item) => [item.provider_slug, item.error_message ?? "Unknown provider error"])
  );
  const scores = Object.fromEntries(
    evidence.map((item) => [
      item.provider_slug,
      item.sub_score != null ? Number(item.sub_score) : null,
    ])
  );

  const raisesEvidence = evidence.find(
    (item) => item.provider_slug === "raises" && item.raw_payload
  );
  const raisesPayload = isRecord(raisesEvidence?.raw_payload) ? raisesEvidence.raw_payload : null;
  const raisesAnalysis = isRecord(raisesPayload?.analysis) ? raisesPayload.analysis : null;
  const raisesList = Array.isArray(raisesAnalysis?.raises)
    ? raisesAnalysis.raises
    : Array.isArray(raisesPayload?.raises)
      ? raisesPayload.raises
      : [];
  const investorSet = new Set<string>();
  for (const raise of raisesList) {
    if (!isRecord(raise)) continue;
    for (const investor of toStringArray(raise.investors)) {
      investorSet.add(investor);
    }
  }
  const allInvestors = Array.from(investorSet);
  const leadInvestors = toStringArray(
    raisesAnalysis?.leadInvestors ?? raisesPayload?.leadInvestors
  );
  const tierCountsPayload = isRecord(raisesPayload?.tierCounts)
    ? raisesPayload.tierCounts
    : isRecord(raisesAnalysis?.tierCounts)
      ? raisesAnalysis.tierCounts
      : null;

  const pedigreeEvidence = evidence.find(
    (item) => item.provider_slug === "pedigree" && item.raw_payload
  );
  const pedigreePayload = isRecord(pedigreeEvidence?.raw_payload)
    ? pedigreeEvidence.raw_payload
    : null;
  const pedigreeAnalysis = isRecord(pedigreePayload?.analysis) ? pedigreePayload.analysis : null;

  return {
    status: toAuditStatusValue(latestRun?.status),
    statusSource: latestRun ? "durable_run" : "none",
    queueState: null,
    queuePosition: null,
    waitingMs: null,
    jobId: null,
    queueLookupSource: "none",
    staleQueueMapping: false,
    latestRun: latestRun
      ? {
          id: latestRun.id,
          status: latestRun.status,
          alphaScore: latestRun.alpha_score_snapshot,
          confidence: latestRun.confidence_snapshot,
          providerSuccessCount: latestRun.provider_success_count,
          providerFailCount: latestRun.provider_fail_count,
          startedAt: latestRun.started_at,
          finishedAt: latestRun.finished_at,
          notes: latestRun.notes,
          triggerSource: latestRun.trigger_source,
          triggerFlags: latestRun.trigger_flags,
        }
      : null,
    providers: {
      completed,
      failed,
      failedDetails,
      total:
        latestRun != null
          ? Math.max(
              Number(latestRun.provider_success_count ?? 0) +
                Number(latestRun.provider_fail_count ?? 0),
              evidence.length
            )
          : evidence.length,
      scores,
    },
    backers:
      raisesPayload || allInvestors.length > 0
        ? {
            totalFunding: toNumberOrNull(
              raisesPayload?.totalFunding ?? raisesAnalysis?.totalFunding
            ),
            latestValuation: toNumberOrNull(
              raisesPayload?.latestValuation ?? raisesAnalysis?.latestValuation
            ),
            latestStage:
              typeof (raisesPayload?.latestStage ?? raisesAnalysis?.latestStage) === "string"
                ? String(raisesPayload?.latestStage ?? raisesAnalysis?.latestStage)
                : null,
            roundCount: toNumberOrNull(raisesPayload?.roundCount ?? raisesAnalysis?.roundCount),
            allInvestors,
            leadInvestors,
            tierCounts: {
              tier1: toNumberOrNull(tierCountsPayload?.tier1) ?? 0,
              tier2: toNumberOrNull(tierCountsPayload?.tier2) ?? 0,
              tier3: toNumberOrNull(tierCountsPayload?.tier3) ?? 0,
              tier1Names: toStringArray(tierCountsPayload?.tier1Names),
              tier2Names: toStringArray(tierCountsPayload?.tier2Names),
              tier3Names: toStringArray(tierCountsPayload?.tier3Names),
            },
          }
        : null,
    founder:
      pedigreePayload || pedigreeAnalysis
        ? {
            founderName:
              typeof (pedigreeAnalysis?.founderName ?? pedigreePayload?.founderName) === "string"
                ? String(pedigreeAnalysis?.founderName ?? pedigreePayload?.founderName)
                : null,
            founderTwitter:
              typeof pedigreeAnalysis?.founderTwitterHandle === "string"
                ? pedigreeAnalysis.founderTwitterHandle
                : null,
            founderTier: toNumberOrNull(pedigreeAnalysis?.founderTier),
            tierReason:
              typeof pedigreeAnalysis?.tierReason === "string" ? pedigreeAnalysis.tierReason : null,
            riskRationale:
              typeof pedigreeAnalysis?.riskRationale === "string"
                ? pedigreeAnalysis.riskRationale
                : null,
            confidence: toNumberOrNull(pedigreeAnalysis?.confidence),
            manualOverride: null,
            founderSocial: isRecord(pedigreePayload?.founderSocial)
              ? {
                  twitterScore: toNumberOrNull(pedigreePayload.founderSocial.twitterScore),
                  moniScore: toNumberOrNull(pedigreePayload.founderSocial.moniScore),
                  combinedScore: toNumberOrNull(pedigreePayload.founderSocial.combinedScore),
                }
              : null,
          }
        : null,
    warmIntros: workflowDetail?.warmLead.hasWarmLead
      ? {
          hasMatches: workflowDetail.warmLead.hasWarmLead,
          matches: workflowDetail.warmLead.matches.map((match) => ({
            fundName: match.fundName,
            contact: match.contact,
          })),
          matchedFundNames: workflowDetail.warmLead.matchedFundNames,
        }
      : null,
  };
}

async function getProjectDetailFallback(
  projectId: string,
  includeWorkflow: boolean
): Promise<ProjectDetailData> {
  const [rankSummary, history, latestAuditRows, workflowDetail] = await Promise.all([
    fetchJson<ProjectRankSummary>(`/v1/projects/${projectId}/rank-summary`),
    fetchJson<ProjectDetailData["history"]>(`/v1/projects/${projectId}/history?days=365`),
    fetchJson<ProjectAuditRunRecord[]>(`/v1/projects/${projectId}/audits?limit=1`),
    includeWorkflow ? getEvWorkstationProjectDetail(projectId) : Promise.resolve(null),
  ]);

  const latestRun = latestAuditRows?.[0] ?? null;
  const evidence = latestRun
    ? ((await fetchJson<AuditEvidence[]>(
        `/v1/projects/${projectId}/audits/${latestRun.id}/evidence`
      )) ?? [])
    : [];

  const detail: ProjectDetailData = {
    history: history ?? [],
    evidence,
    auditStatus: buildFallbackAuditStatus(latestRun, evidence, workflowDetail),
    rankSummary,
    workflowDetail,
  };

  return detail;
}

export async function getProjectDetail(
  projectId: string,
  includeWorkflow = false
): Promise<ProjectDetailData> {
  const suffix = includeWorkflow ? "?includeWorkflow=true" : "";

  try {
    const res = await apiRequest(`/v1/projects/${projectId}/detail${suffix}`);

    if (!res.ok) {
      const errBody = (await res.json().catch(() => ({}))) as {
        error?: { message?: string };
      };
      throw new Error(errBody.error?.message ?? "Failed to load project detail");
    }

    const payload = (await res.json()) as ApiResponse<ProjectDetailData>;
    return payload.data ?? emptyProjectDetailData();
  } catch (error) {
    console.warn("[project-detail] Falling back to split detail reads", error);
    const fallback = await getProjectDetailFallback(projectId, includeWorkflow);
    if (
      fallback.history.length > 0 ||
      fallback.evidence.length > 0 ||
      fallback.auditStatus !== null ||
      fallback.rankSummary !== null ||
      fallback.workflowDetail !== null
    ) {
      return fallback;
    }
    throw error;
  }
}
